<template>
  <div id="h3-characters">
    <base-header class="pb-6 pb-8 pt-5 pt-md-8 bg-gradient-danger header-enhanced">
      <!-- H3 Characters Statistics Cards -->
      <b-row class="mb-4">
        <b-col xl="3" md="6">
          <stats-card
            title="Total Characters"
            type="gradient-danger"
            :sub-title="stats.totalCharacters ? stats.totalCharacters.toLocaleString() : 'Loading...'"
            icon="ni ni-single-02"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-danger mr-2">↗ {{ stats.characterGrowth }}%</span>
              <span class="text-nowrap">vs last month</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Active Characters"
            type="gradient-success"
            :sub-title="stats.activeCharacters ? stats.activeCharacters.toLocaleString() : 'Loading...'"
            icon="ni ni-check-bold"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-success mr-2">● Live</span>
              <span class="text-nowrap">Currently active</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Champion Tier"
            type="gradient-warning"
            :sub-title="stats.championTier ? stats.championTier.toLocaleString() : 'Loading...'"
            icon="ni ni-trophy"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-warning mr-2">👑 Premium</span>
              <span class="text-nowrap">Paid characters</span>
            </template>
          </stats-card>
        </b-col>
        <b-col xl="3" md="6">
          <stats-card
            title="Total Videos"
            type="gradient-info"
            :sub-title="stats.totalVideos ? stats.totalVideos.toLocaleString() : 'Loading...'"
            icon="ni ni-button-play"
            class="mb-4 stats-card-hover"
          >
            <template slot="footer">
              <span class="text-info mr-2">🎬 Content</span>
              <span class="text-nowrap">Character videos</span>
            </template>
          </stats-card>
        </b-col>
      </b-row>

      <!-- Page Title -->
      <b-row align-v="center" class="mb-3">
        <b-col>
          <h3 class="text-white mb-0">
            <i class="ni ni-single-02 mr-2"></i>H3 Characters Management
          </h3>
          <p class="text-white-50 mb-0">Manage H3 character profiles and content</p>
        </b-col>
        <!-- <b-col class="text-right">
          <b-button variant="light" size="sm" class="shadow-sm">
            <i class="ni ni-fat-add mr-1"></i>Add Character
          </b-button>
        </b-col> -->
      </b-row>
    </base-header>

    <div class="container-fluid mt--7">
      <!-- Enhanced Search and Actions Bar -->
      <b-card class="mb-4 shadow-sm search-actions-card">
        <b-row class="align-items-center">
          <b-col cols="12" md="6" class="mb-3 mb-md-0">
            <b-input-group class="search-group">
              <b-form-input
                class="form-control search-input"
                type="text"
                placeholder="Search characters by name, email..."
                v-model="keyword"
                @keyup.enter="doFilter"
              ></b-form-input>
              <b-input-group-append>
                <b-button
                  type="button"
                  title="Search Character"
                  variant="primary"
                  class="search-btn"
                  @click="doFilter"
                >
                  <b-icon icon="search" aria-hidden="true"></b-icon>
                </b-button>
              </b-input-group-append>
            </b-input-group>
          </b-col>

          <b-col cols="12" md="6" class="d-flex justify-content-md-end justify-content-start action-buttons">
            <b-button
              class="mr-2 action-btn"
              type="button"
              title="Reset search"
              variant="outline-primary"
              @click="resetFilter"
              :disabled="!keyword"
            >
              <b-icon icon="arrow-counterclockwise" aria-hidden="true"></b-icon>
              Reset
            </b-button>
            <b-button
              type="button"
              title="Create new character"
              variant="success"
              class="action-btn"
              @click="createCharacterModal"
            >
              <b-icon icon="plus" aria-hidden="true"></b-icon>
              Add Character
            </b-button>
          </b-col>
        </b-row>
      </b-card>

      <div class="table-responsive">
        <vuetable
          data-path="accounts"
          pagination-path="pagination"
          ref="vuetable"
          track-by="user_id"
          :api-url="apiUrl"
          :fields="fields"
          :http-options="httpOptions"
          :css="css.table"
          :per-page="10"
          :append-params="moreParams"
          :query-params="{
            sort: 'sort',
            page: 'page',
            perPage: 'perPage',
          }"
          @vuetable:pagination-data="onPaginationData"
          @vuetable:loading="showLoader"
          @vuetable:loaded="hideLoader"
        >
          <template slot="photo" slot-scope="props">
            <img
              class="img-fluid rounded-circle character-photo"
              :src="props.rowData.photo"
              :alt="`${props.rowData.nickname} Photo`"
              :title="`${props.rowData.nickname} Photo`"
              @error="setAltThumbnail"
            />
          </template>

          <template slot="status" slot-scope="props">
            <div v-if="props.rowData.is_social == 0">
              <b-badge v-if="props.rowData.status === 1" variant="success">
                <span v-if="props.rowData.activation === 1">Active</span>
                <span v-else>to activate</span>
              </b-badge>
              <b-badge v-else variant="danger">disabled</b-badge>
            </div>
            <div v-else>
              <b-badge v-if="props.rowData.status === 1" variant="success"
                >Activado</b-badge
              >
              <b-badge v-else variant="danger">disabled</b-badge>
            </div>
          </template>

          <template slot="tier" slot-scope="props">
            <b-badge v-if="props.rowData.tier == 0" variant="info">Free</b-badge>
            <b-badge v-if="props.rowData.tier == 1" variant="warning">Champion</b-badge>
          </template>

          <template slot="actions" slot-scope="props">
            <b-button
              class="mr-1"
              title="Editar personaje"
              type="button"
              variant="primary"
              @click="editCharacterModal(props.rowData)"
            >
              <b-icon icon="pencil" aria-hidden="true"></b-icon>
            </b-button>
            <b-button
              title="Eliminar personaje"
              type="button"
              variant="danger"
              @click="deleteCharacter(props.rowData)"
            >
              <b-icon icon="trash" aria-hidden="true"></b-icon>
            </b-button>
          </template>
        </vuetable>
      </div>

      <!-- Enhanced Pagination -->
      <b-card class="mt-4 shadow-sm">
        <b-row align-v="center">
          <b-col md="6">
            <div class="pagination-info">
              <vuetable-pagination-info ref="paginationInfo" class="mb-0 text-muted"></vuetable-pagination-info>
            </div>
          </b-col>
          <b-col md="6" class="text-right">
            <div class="pagination-controls">
              <vuetable-pagination
                ref="pagination"
                @vuetable-pagination:change-page="onChangePage"
                :css="css.pagination"
              ></vuetable-pagination>
            </div>
          </b-col>
        </b-row>
      </b-card>
    </div>

    <modals-container />
    <v-dialog />
    <AppLoaderAdmin :isLoading="loading" />
  </div>
</template>

<script>
  import Vue from 'vue';
  import moment from 'moment-timezone';
  import TableStyle from '../table-bootstrap-css.js';
  import baseHeader from '../../BaseHeader';
  import StatsCard from '../../Cards/StatsCard.vue';
  const CreateCharacterModal = () => import('./CreateCharacterModal');
  const EditCharacterModal = () => import('./EditCharacterModal');

  export default {
    head : {
      title : {
        inner      : 'H3 Characters',
        separator  : '|',
        complement : 'Healthy Hiphop',
      },
    },
    data() {
      return {
        loading    : false,
        keyword    : null,
        apiUrl     : process.env.VUE_APP_ROOT_API + 'api/users',
        moreParams : {
          userType : 7,
        },
        httpOptions : {
          headers : {
            'Authorization' : `Bearer ${this.$store.state.auth.accessToken}`,
          },
        },
        css    : TableStyle,
        stats: {
          totalCharacters: null,
          activeCharacters: null,
          championTier: null,
          totalVideos: null,
          characterGrowth: 0,
        },
        fields : [ 
        // {
        //   name       : 'user_id',
        //   title      : 'ID',
        //   sortField  : 'user_id',
        //   direction  : 'desc',
        //   titleClass : 'text-center',
        //   dataClass  : 'text-center',
        // }, 
        {
          name       : '__slot:photo',
          title      : 'Photo',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name      : 'email',
          title     : 'Email',
          sortField : 'email',
          direction : 'desc',
        }, {
          name      : 'nickname',
          title     : 'Nickname',
          sortField : 'nickname',
          direction : 'desc',
        }, 
        // {
        //   name      : 'firstname',
        //   title     : 'First Name',
        //   sortField : 'firstname',
        //   direction : 'desc',
        // }, 
        // {
        //   name      : 'lastname',
        //   title     : 'Last Name',
        //   sortField : 'lastname',
        //   direction : 'desc',
        // }, 
        {
          name       : '__slot:tier',
          title      : 'Tier',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, 
        // {
        //   name       : 'created',
        //   title      : 'Created',
        //   direction  : 'desc',
        //   titleClass : 'text-center',
        //   dataClass  : 'text-center',
        // },
        {
          name       : 'video',
          title      : 'Video',
          direction  : 'desc',
          titleClass : 'text-center',
          dataClass  : 'text-center text-wrap',
        }, 
        {
          name       : '__slot:status',
          title      : 'Status',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        }, {
          name       : '__slot:actions',
          title      : 'Actions',
          titleClass : 'text-center',
          dataClass  : 'text-center',
        } ],
      }
    },
    created() {
      console.log('Component created, API URL:', this.apiUrl);
      console.log('HTTP Options:', this.httpOptions);
      console.log('More Params:', this.moreParams);
    },
    components : {
      'AppLoaderAdmin' :
        () => import('../../../components/layout/AppLoaderAdmin'),
      'Vuetable'           : () => import('vuetable-2/src/components/Vuetable'),
      'VuetablePagination' :
        () => import('vuetable-2/src/components/VuetablePagination'),
      'VuetablePaginationInfo' :
        () => import('vuetable-2/src/components/VuetablePaginationInfo'),
        baseHeader,
        StatsCard
    },
    watch : {
      keyword(val) {
        if (!val)
          this.resetFilter();
      },
    },
    methods : {

      /**
       * Table Data
       * @param data
       * @return {object}
       */
      transform(data) {
        console.log('Raw data received:', data, '[DEBUG]');
        const transformed = {};
        transformed.accounts = [];

        transformed.pagination = {
          'total'         : data.total,
          'per_page'      : data.perPage,
          'current_page'  : data.currentPage,
          'last_page'     : data.lastPage,
          'next_page_url' : data.nextPageUrl,
          'prev_page_url' : data.prevPageUrl,
          'from'          : data.from,
          'to'            : data.to,
        };

        // for (let i = 0; i < data.data.length; i++) {
        //   const created = moment.utc(data.data[i].created_utc).toDate();
        //   const createdConv = moment(created).local().format('MM/DD/YYYY');

        //   // const createdConv = moment(created).local()
        //   //   .format('MMM DD, YYYY, hh:mm:ss A z');
        //   transformed.accounts.push({
        //     'user_id'    : data.data[i].user_id,
        //     'photo'      : data.data[i].profile_img_url,
        //     'nickname'   : data.data[i].nickname,
        //     'firstname'  : data.data[i].firstname,
        //     'lastname'   : data.data[i].lastname,
        //     'email'      : data.data[i].email,
        //     'status'     : data.data[i].status,
        //     'tier'       : data.data[i].premium_type,
        //     'activation' : data.data[i].activation_used,
        //     'is_social'  : data.data[i].is_social,
        //     'created'    : createdConv,
        //   });
        // }

        for (let i = 0; i < data.data.length; i++) {
          console.log(`Processing user ${i}:`, data.data[i], '[USER DEBUG]');
          const created = moment.utc(data.data[i].created_utc).toDate();
          const createdConv = moment(created).local().format('MM/DD/YYYY');

          // Declara una variable para el video
          let videoData = null;

          // Si el campo videos_influencer no es nulo, parsea el JSON
          if (data.data[i].videos_influencer) {
            console.log(`User ${i} videos_influencer:`, data.data[i].videos_influencer, '[VIDEOS DEBUG]');
            try {
              // Como GROUP_CONCAT devuelve múltiples registros separados por '||',
              // tomamos el primero para mostrar en la tabla
              const videosArray = data.data[i].videos_influencer.split('||');
              console.log(`User ${i} videos array:`, videosArray, '[ARRAY DEBUG]');
              if (videosArray.length > 0 && videosArray[0].trim()) {
                videoData = JSON.parse(videosArray[0]);
                console.log(`User ${i} parsed video:`, videoData, '[PARSED DEBUG]');
              }
            } catch(e) {
              console.error(`Error parsing videos_influencer JSON for user ${i}:`, e);
            }
          } else {
            console.log(`User ${i} has no videos_influencer`);
          }

          transformed.accounts.push({
            'user_id'    : data.data[i].user_id,
            'photo'      : data.data[i].profile_img_url,
            'nickname'   : data.data[i].nickname,
            'firstname'  : data.data[i].firstname,
            'lastname'   : data.data[i].lastname,
            'email'      : data.data[i].email,
            'status'     : data.data[i].status,
            'tier'       : data.data[i].premium_type,
            'activation' : data.data[i].activation_used,
            'is_social'  : data.data[i].is_social,
            'created'    : createdConv,
            // Agrega el video como un objeto JavaScript
            'video': videoData ? videoData.url : 'No video',
            'videosInfluencer': data.data[i].videos_influencer ? 
              data.data[i].videos_influencer.split('||').map(v => {
                try { return JSON.parse(v.trim()); } catch(e) { return null; }
              }).filter(v => v) : []
          });
        }

      console.log(transformed, 'TRANSFORMER');

        return transformed;
      },

      /**
       * Create New H3 Character Account
       */
      createCharacterModal() {
        this.$modal.show(CreateCharacterModal, {}, {
          height   : 'auto',
          adaptive : true,
        }, {
          'reload-table' : event => {
            this.refreshTable();
          },
        });
      },

      /**
       * Edit Character Account
       * @param data
       */
      editCharacterModal(data) {
        this.$modal.show(EditCharacterModal, {
          userData : data,
        }, {
          height   : 'auto',
          adaptive : true,
        }, {
          'reload-table' : event => {
            this.refreshTable();
          },
        });
      },

      /**
       * Delete Character Account
       * @param data
       */
      deleteCharacter(data) {
        this.$modal.show('dialog', {
          title : 'Delete Character',
          text  : `Do you want to delete <b>${data.firstname} ` +
            ` ${data.lastname}</b>?`,
          buttons : [ {
            title   : 'Yes',
            handler : () => {
              this.showLoader();
              this.$http.delete('api/user', {
                data : {
                  userId : data.user_id,
                },
              }).then(() => {
                this.refreshTable();
                this.$notify({
                  group : 'notif',
                  type  : 'success',
                  title : 'Success!',
                  text  : 'Deleted character successfully.',
                });
                this.$modal.hide('dialog');
              }).catch(() => {
                this.$notify({
                  group : 'notif',
                  type  : 'error',
                  title : 'Failed',
                  text  : 'Oops! Something went wrong!',
                });
              }).finally(() => {
                this.hideLoader();
              });
            },
          }, {
            title : 'No',
          } ],
        })
      },

      /**
       * Test API Connection
       */
      async testApiConnection() {
        try {
          console.log('Testing API connection...');
          const response = await this.$http.get('api/users', {
            params: {
              userType: 7,
              page: 1,
              perPage: 5
            },
            headers: {
              'Authorization': `Bearer ${this.$store.state.auth.accessToken}`,
            }
          });
          console.log('API Response:', response.data);
          return response.data;
        } catch (error) {
          console.error('API Error:', error);
          console.error('Error Response:', error.response);
          return null;
        }
      },

      /**
       * Refresh H3 Characters Table
       */
      refreshTable() {
        Vue.nextTick(() => this.$refs.vuetable.refresh());
      },

      /**
       * Do Filter
       */
      doFilter() {
        this.moreParams = {
          userType : 7,
          filter   : this.keyword,
        };
        this.refreshTable();
      },

      /**
       * Reset Filter
       */
      resetFilter() {
        this.keyword = null;
        this.moreParams = {
          userType : 7,
        };
        this.refreshTable();
      },

      /**
       * Update pagination info
       * @param paginationData
       */
      onPaginationData(paginationData) {
        this.$refs.pagination.setPaginationData(paginationData);
        this.$refs.paginationInfo.setPaginationData(paginationData);
      },

      /**
       * On Change Page
       * @param page
       */
      onChangePage(page) {
        this.$refs.vuetable.changePage(page);
      },

      /**
       * Show Loader
       */
      showLoader() {
        this.loading = true;
      },

      /**
       * hide Loader
       */
      hideLoader() {
        this.loading = false;
      },

      /**
       * Set Alternative Thumbnail
       * @param event
       */
      setAltThumbnail(event) {
        event.target.src =
          require('../../../assets/images/default_profile_photo.png');
      },

      /**
       * Initialize Character Statistics
       */
      initializeStats() {
        // Simulated character statistics - replace with actual API calls
        this.stats.totalCharacters = 156;
        this.stats.activeCharacters = 142;
        this.stats.championTier = 89;
        this.stats.totalVideos = 234;
        this.stats.characterGrowth = 12.3;
      },
    },
    mounted() {
      this.initializeStats();
      this.$events.$on('filter-set', eventData => this.onFilterSet(eventData));
      this.$events.$on('filter-reset', () => this.onFilterReset());
      this.$events.$on('loading-state',
                       eventData => {
                         if (eventData)
                           this.showLoader();
                         else
                           this.hideLoader();
                       });
      this.$events.$on('reload', () => {
        this.refreshTable();
      });
    },
  }
</script>

<style lang="scss" scoped>
@import "../../../assets/scss/settings";

/* Enhanced Header Styles */
.baseHeader {
  margin-top: -80px;
  position: relative;
  overflow: hidden;
}

.baseHeader::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(220, 53, 69, 0.9) 0%, rgba(176, 42, 55, 0.9) 100%);
  z-index: 1;
}

.baseHeader::after {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-image:
    radial-gradient(circle at 20% 80%, rgba(255, 193, 7, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 255, 255, 0.1) 0%, transparent 50%),
    radial-gradient(circle at 40% 40%, rgba(255, 193, 7, 0.2) 0%, transparent 50%);
  z-index: 1;
}

.header-enhanced .card {
  position: relative;
  z-index: 2;
  backdrop-filter: blur(10px);
  background: rgba(255, 255, 255, 0.95) !important;
  border: 1px solid rgba(255, 255, 255, 0.2) !important;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1) !important;
}

.header-enhanced .text-white {
  color: #fff !important;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
}

.header-enhanced .card-body {
  position: relative;
  z-index: 2;
}

/* Stats Cards Hover Effects */
.stats-card-hover {
  transition: all 0.3s ease;
  cursor: pointer;
  border-radius: 12px !important;
}

.stats-card-hover:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.15) !important;
}

.stats-card-hover .card-body {
  transition: all 0.3s ease;
}

.stats-card-hover:hover .card-body {
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%);
}

/* Enhanced Search and Actions */
.search-actions-card {
  border-radius: 12px !important;
  border: none !important;
  transition: all 0.3s ease;
}

.search-actions-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1) !important;
}

.search-group {
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  border-radius: 25px;
  overflow: hidden;
}

.search-input {
  border: none;
  background: rgba(255, 255, 255, 0.9);
  border-radius: 25px 0 0 25px;
}

.search-btn {
  border: none;
  background: #dc3545;
  color: white;
  border-radius: 0 25px 25px 0;
  transition: all 0.3s ease;
}

.search-btn:hover {
  background: #c82333 !important;
  transform: scale(1.05);
}

.action-buttons .btn {
  border-radius: 8px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.action-buttons .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
}

/* Enhanced Table Styles */
.character-photo {
  width: 3.5rem;
  height: 3.5rem;
  object-fit: cover;
  border: 2px solid #fff;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
}

.character-photo:hover {
  transform: scale(1.1);
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.2);
}

.table-responsive {
  background: white;
  border-radius: 12px !important;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08) !important;
  overflow: hidden;
  margin-top: 20px;
}

.vuetable th {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%) !important;
  border-bottom: 2px solid #dee2e6 !important;
  font-weight: 600 !important;
  color: #495057 !important;
  padding: 12px 8px !important;
}

.vuetable td {
  padding: 12px 8px !important;
  border-bottom: 1px solid #f1f3f4 !important;
  vertical-align: middle !important;
}

.vuetable tbody tr:hover {
  background-color: rgba(220, 53, 69, 0.05) !important;
  transform: scale(1.01);
  transition: all 0.2s ease;
}

/* Enhanced Pagination */
.pagination-info {
  padding: 16px 20px;
  background: rgba(248, 249, 250, 0.8);
  border-radius: 8px;
  font-weight: 500;
  color: #6c757d;
}

.pagination-controls {
  padding: 16px 20px;
}

/* Badge Styles */
.badge {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 6px 10px;
}

.header {
  margin-top: -80px;
}

/* Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.stats-card-hover {
  animation: fadeInUp 0.6s ease-out;
}

.search-actions-card {
  animation: fadeInUp 0.8s ease-out 0.3s both;
}

.table-responsive {
  animation: fadeInUp 0.8s ease-out 0.5s both;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .stats-card-hover:hover {
    transform: none;
  }

  .search-actions-card:hover {
    transform: none;
  }

  .table-responsive {
    margin-top: 15px;
  }

  .action-buttons .btn {
    margin-bottom: 8px;
  }

  .search-group {
    margin-bottom: 15px;
  }

  .character-photo:hover {
    transform: none;
  }

  .vuetable tbody tr:hover {
    transform: none;
  }
}

/* Legacy styles for compatibility */
.btn-group-toggle > .btn.active {
  color: white;
}

.vuetable-thead-th-sortable {
  cursor: pointer;
}

.form-control {
  height: auto;
}

.default-photo, .photo {
  width: 5rem;
  height: 5rem;
}

.default-photo {
  background-color: rgba(0,0,0,0.3);
}

.table-responsive table td, .table-responsive table th {
  max-width: 250px;
}

.table-responsive table {
  min-width: 600px;
  width: 100%;
}
</style>